import type { DynamicFormSchema } from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
import { parseFormLayoutUnknown } from "@/lib/layoutRenderer/schemas/layout.schema";
import type {
	FormDefinition,
	FormDefinitionQueryData,
} from "@/services/form-definition";
import { resolveFormRulesFromFormDefinition } from "@/services/form-definition/resolve-form-definition-form-rules.util";
import type { FormRule } from "../types/form-rule-types";
import { enrichDefinitionsWithLayoutMetadata } from "./api-form-definition-payload.util";
import {
	buildDynamicFormSchemaFromApiFormDefinition,
	listMissingFieldDefinitionIdsForLayout,
} from "./form-definition-api-to-schema.util";

/**
 * Builds a {@link DynamicFormSchema} ready for the form editor store's `hydrateFromSchema`
 * from GET `/form-definitions/current` (or equivalent) row.
 *
 * @returns `undefined` when layout JSON is unusable, layout parse fails,
 *          or any layout `Field` node references a definition not in the resolved set.
 */
export function prepareFormDefinitionEditorSchema(
	formSchemaId: string,
	api: FormDefinition | FormDefinitionQueryData,
): DynamicFormSchema | undefined {
	const built = buildDynamicFormSchemaFromApiFormDefinition(formSchemaId, api);
	if (built == null) return undefined;

	const layoutParsed = parseFormLayoutUnknown(built.schema.layoutJson);
	if (!layoutParsed.ok) return undefined;

	const fieldDefinitions = enrichDefinitionsWithLayoutMetadata(
		built.schema.fieldDefinitions,
		layoutParsed.layout,
	);

	const missing = listMissingFieldDefinitionIdsForLayout(
		layoutParsed.layout,
		fieldDefinitions,
	);
	if (missing.length > 0) return undefined;

	return {
		...built.schema,
		fieldDefinitions,
	};
}

/**
 * Parallel to {@link prepareFormDefinitionEditorSchema}: resolves form rules from the
 * top-level API field with legacy `layoutJson` envelope fallback.
 */
export function prepareFormDefinitionEditorFormRules(
	api: FormDefinition | FormDefinitionQueryData,
): FormRule[] {
	return resolveFormRulesFromFormDefinition(api);
}

/** @deprecated Use {@link prepareFormDefinitionEditorSchema} */
export const prepareTicketTypeEditorSchema = prepareFormDefinitionEditorSchema;
